import { AppNavParams } from '@euler/app/Routes';
import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { AuthContext } from '@euler/app/flows/auth/context';
import { linking as normalModeLinking } from '@euler/app/navigation/linking.normal';
import { linking as presentationModeLinking } from '@euler/app/navigation/linking.presentation';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Colors } from '@euler/components/Colors';
import { NavigationContainerRefProvider } from '@euler/functions/navigation/NavigationContainerRefContext';
import { useTrackNavigator } from '@euler/functions/navigatorTracking';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { delayed, makeDebug, onErrorIgnore } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import {
  DefaultTheme,
  NavigationContainer,
  NavigationContainerRefWithCurrent,
  NavigationState,
  PartialState,
  getActionFromState,
  getStateFromPath,
  useNavigationContainerRef,
} from '@react-navigation/native';
import * as Linking from 'expo-linking';
import qs from 'qs';
import React, {
  ReactNode,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { rewriteAuthedPath } from './rewriteAuthedPath';

const debug = makeDebug('linking');

// eslint-disable-next-line @typescript-eslint/init-declarations
let __globalNavigationRef: ReturnType<typeof useNavigationContainerRef>;

export function getGlobalNavigationRef() {
  return __globalNavigationRef as unknown as NavigationContainerRefWithCurrent<AppNavParams>;
}

export const RoutesContainer = memo(
  (props: { children: ReactNode; onReady?: () => void }) => {
    const authContext = useContext(AuthContext);
    const presentationMode = usePresentationMode();
    const { page } = usePresentationTheme();
    const [authState] = useBehaviorSubject(authContext.authState$);
    const isAuthenticated = authState.isAuthenticated;
    const productLine = authState.isAuthenticated
      ? authState.authenticatedUserInfo.store.editionProductLine
      : undefined;
    const initialRequestedPath = useRef<string | undefined>(undefined);
    const normalLinking = useMemo<typeof normalModeLinking>(
      () => ({
        ...normalModeLinking,
        normal: {
          ...normalModeLinking.normal,
          getStateFromPath: (path, options) =>
            normalModeLinking.normal.getStateFromPath!(
              rewriteAuthedPath(path, productLine),
              options,
            ),
        },
      }),
      [productLine],
    );
    const linking = useMemo(
      () =>
        presentationMode === 'normal' ? normalLinking : presentationModeLinking,
      [presentationMode, normalLinking],
    );
    debug('presentation mode:', presentationMode);

    const navigationRef = useNavigationContainerRef();
    __globalNavigationRef = navigationRef;

    const { onStateChange, onReady, ref } = useTrackNavigator(
      '',
      navigationRef,
      () => {
        props.onReady?.();
      },
    );

    const isMobile = useIsMobileLayout();
    const [isReady, setIsReady] = useState(false);
    const [initialStateHandled, setInitialStateHandled] = useState(false);
    const [initialState, setInitialState] =
      useState<PartialState<NavigationState>>();

    const onUrlOpen = usePersistFn(({ url }: { url: string }) => {
      debug('url opened: %s', url);

      // todo(eric): ensure home screen is always loaded before navigating to
      // target screens, which is especially important for web right now.
      if (!url || url.includes('/wechat/') || isAuthenticated) {
        return;
      }

      const { path, queryParams } = Linking.parse(url);
      const query = qs.stringify(queryParams);
      const originalUrl = path + (query ? `?${query}` : '');
      const state = getStateFromPath(originalUrl, linking.normal.config);
      const route = state?.routes[state.index ?? 0]?.name;
      if (state && route !== 'NotFound' && !initialState) {
        // remember the initial state and will transition to it when
        // the authentication is successfully done.
        debug(
          'saved initial state: %O, for initial url opened: %s',
          state,
          originalUrl,
        );
        initialRequestedPath.current = originalUrl;
        setInitialState(state);
      }
    });

    useEffect(() => {
      const listener = Linking.addEventListener('url', onUrlOpen);
      return () => {
        listener.remove();
      };
    }, [onUrlOpen]);

    useEffect(() => {
      if (isAuthenticated === undefined) return;

      if (isAuthenticated) {
        // if the user has been authenticated, just let it go
        // the react navigation will handle the linking automatically.
        setInitialStateHandled(true);
        setIsReady(true);
        return;
      }

      // otherwise, we have to determine the continuation logic according
      // to the initial url passed to the app.
      Linking.getInitialURL()
        .then(url => {
          if (url) {
            debug('initial url loaded: %s', url);
          }
          url && onUrlOpen({ url });
        })
        .catch(onErrorIgnore)
        .finally(() => {
          setIsReady(true);
        });
    }, [isAuthenticated, navigationRef, onUrlOpen]);

    useEffect(() => {
      if (!isReady || !navigationRef.isReady() || initialStateHandled) return;

      setInitialStateHandled(true);

      debug(
        'handle initial url state: %O, initialStateHandled: %s',
        initialState,
        initialStateHandled,
      );

      if (!isAuthenticated) {
        // force to the login screen.
        const route = navigationRef.getCurrentRoute();
        if (route?.name !== 'Login' && initialState) {
          const state = getStateFromPath('login', linking.auth.config);
          if (state) {
            const action = getActionFromState(
              state as PartialState<NavigationState>,
              linking.auth.config,
            );
            if (action) {
              navigationRef.dispatch(action);
            }
          }
        }
      } else {
        debug('authenticated, initial state: %O', initialState);
        if (initialState) {
          const requested = initialRequestedPath.current;
          // Through the linking's own resolver, so a page opened before
          // login gets the history a deep link builds (`withAiHistory`).
          const resolve = linking.normal.getStateFromPath ?? getStateFromPath;
          const targetState = requested
            ? resolve(
                rewriteAuthedPath(requested, productLine),
                linking.normal.config,
              ) ?? initialState
            : initialState;
          const action = getActionFromState(
            targetState as PartialState<NavigationState>,
            linking.normal.config,
          );
          if (action) {
            delayed(() => {
              navigationRef.dispatch(action);
              setInitialState(undefined);
            }, 300);
          }
        }
      }
    }, [
      initialState,
      productLine,
      isAuthenticated,
      isReady,
      navigationRef,
      initialStateHandled,
      linking,
    ]);

    // wait until authentication status is successfully resolved.
    if (isAuthenticated === undefined) return null;

    return (
      <NavigationContainer
        key={presentationMode}
        theme={{
          dark: false,
          colors: {
            ...DefaultTheme.colors,
            primary: Colors.Gray2,
            background:
              presentationMode === 'presentation'
                ? page.bg
                : isMobile
                ? '#f5f6f8'
                : DefaultTheme.colors.background,
          },
        }}
        ref={ref}
        linking={isAuthenticated ? linking.normal : linking.auth}
        onReady={onReady}
        onStateChange={onStateChange}
        documentTitle={{
          enabled: false,
        }}
      >
        <NavigationContainerRefProvider navigationRef={navigationRef}>
          {props.children}
        </NavigationContainerRefProvider>
      </NavigationContainer>
    );
  },
);
